import type { ReactNode } from 'react';

/**
 * Centred card shell for the public pages (signup, activation, recovery, invitations). Shares the
 * sign-in screen's classes so the whole front door looks like one thing.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  note,
  width = 'narrow',
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Cross-links, e.g. "Already have an account? Sign in". */
  footer?: ReactNode;
  /** Small print under the card body. */
  note?: ReactNode;
  width?: 'narrow' | 'wide';
}): JSX.Element {
  return (
    <div className="signin-shell">
      <main className={`signin-card ${width === 'wide' ? 'signin-card-wide' : ''}`.trim()}>
        <div className="signin-brand">
          <span className="brand-mark" aria-hidden="true">
            iE
          </span>
          <div>
            <h1 className="signin-title">{title}</h1>
            {subtitle ? <p className="signin-sub">{subtitle}</p> : null}
          </div>
        </div>

        {children}

        {footer ? <div className="auth-links">{footer}</div> : null}
        {note ? <p className="signin-note">{note}</p> : null}
      </main>
    </div>
  );
}

/** Centred icon + message block used for the activation / confirmation outcomes. */
export function AuthStatus({
  tone,
  title,
  children,
  actions,
}: {
  tone: 'good' | 'bad' | 'info' | 'warn';
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}): JSX.Element {
  const glyph = tone === 'good' ? '✓' : tone === 'bad' ? '!' : tone === 'warn' ? '!' : '✉';
  return (
    <div className={`auth-status auth-status-${tone}`} role={tone === 'bad' ? 'alert' : 'status'}>
      <span className="auth-status-icon" aria-hidden="true">
        {glyph}
      </span>
      <p className="auth-status-title">{title}</p>
      {children ? <div className="auth-status-body">{children}</div> : null}
      {actions ? <div className="auth-actions">{actions}</div> : null}
    </div>
  );
}
